/**
 * audioResampler.ts
 *
 * Resamples audio from any browser sample rate (typically 44.1 kHz or 48 kHz)
 * down to 16 kHz using the browser-native `OfflineAudioContext` renderer.
 *
 * This approach is more accurate than a manual polynomial/linear interpolation
 * because the browser uses its own high-quality SRC (sample-rate conversion)
 * internally.
 *
 * Usage:
 *   const pcm16k = await resampleTo16k(float32Buffer, audioContext.sampleRate);
 */

const TARGET_SAMPLE_RATE = 16000;

/**
 * Downsample (or upsample) a Float32Array from `srcRate` Hz to 16 000 Hz.
 *
 * @param buffer  Mono Float32Array of audio samples.
 * @param srcRate Original sample rate in Hz (e.g. 44100 or 48000).
 * @returns       Resampled Float32Array at 16 000 Hz.
 */
export async function resampleTo16k(
  buffer: Float32Array,
  srcRate: number
): Promise<Float32Array> {
  if (srcRate === TARGET_SAMPLE_RATE) {
    return buffer;
  }

  const targetLength = Math.round(
    (buffer.length * TARGET_SAMPLE_RATE) / srcRate
  );

  const offlineCtx = new OfflineAudioContext(
    1,               // mono
    targetLength,
    TARGET_SAMPLE_RATE
  );

  // Create an AudioBuffer from the source samples.
  // copyToChannel requires a Float32Array backed by a plain ArrayBuffer (not
  // SharedArrayBuffer), so we always copy into a fresh plain array first.
  const srcBuffer = offlineCtx.createBuffer(1, buffer.length, srcRate);
  const plainCopy = new Float32Array(buffer.length);
  plainCopy.set(buffer);
  srcBuffer.copyToChannel(plainCopy, 0);

  // Connect a buffer source and let the offline context render it
  const source = offlineCtx.createBufferSource();
  source.buffer = srcBuffer;
  source.connect(offlineCtx.destination);
  source.start(0);

  const rendered = await offlineCtx.startRendering();
  return rendered.getChannelData(0);
}
